import React, { useEffect, useState, useCallback } from "react";
import { RotateCw } from "lucide-react";
import ToolbarSearch from "../components/ToolbarSearch";
import { linkifyPhoneText } from "../utils/linkifyPhone";
import "../styles/operator-info.css";
import { apiFetch } from "../utils/apiFetch";
import { markOperatorInfoViewed } from "../services/operator_info";
import { API_BASE_CLIENT } from "../config/vars";

const PREVIEW_LIMIT = 140;

const OperatorInfoClients = ({ onOperatorInfoViewed }) => {
    const [infoList, setInfoList] = useState([]);
    const [filteredInfo, setFilteredInfo] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [message, setMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");

    const loadInfo = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_BASE_CLIENT}/operator-info/client`);
            if (!res.ok) throw new Error("Не вдалося завантажити дані");
            const data = await res.json();
            setInfoList(data);
            setFilteredInfo(data);
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    }, []);

    useEffect(() => {
        loadInfo();
    }, [loadInfo]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await markOperatorInfoViewed();
                if (!cancelled && typeof onOperatorInfoViewed === "function") onOperatorInfoViewed();
            } catch (_) {
                // ignore mark failures
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!Array.isArray(infoList) || infoList.length === 0) return;

        let raw = null;
        try {
            raw = localStorage.getItem("pendingOperatorInfoOpenId_client");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        const id = parseInt(raw, 10);
        try {
            localStorage.removeItem("pendingOperatorInfoOpenId_client");
        } catch (_) {
            // ignore
        }
        if (!Number.isFinite(id) || id <= 0) return;

        const item = infoList.find((x) => Number(x?.id) === id);
        if (item) {
            setExpandedId(item.id);
        }
    }, [infoList]);

    useEffect(() => {
        const filtered = infoList.filter((item) => {
            const searchLower = searchQuery.toLowerCase();
            return (
                String(item.name).toLowerCase().includes(searchLower) ||
                String(item.description).toLowerCase().includes(searchLower) ||
                String(item.location_name || "").toLowerCase().includes(searchLower)
            );
        });
        setFilteredInfo(filtered);
    }, [searchQuery, infoList]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredInfo) return [];
        const sorted = [...filteredInfo];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            }
            return valA < valB ? 1 : valA > valB ? -1 : 0;
        });
        return sorted;
    };

    const normalizeText = (value) => String(value || "").replace(/\r\n/g, "\n").trim();

    const isLongContent = (value) => {
        const raw = normalizeText(value);
        return raw.length > PREVIEW_LIMIT || raw.includes("\n");
    };

    const getPreviewText = (value) => {
        const raw = normalizeText(value);
        if (!raw) return "Без опису";
        const singleLine = raw.replace(/\n+/g, " ");
        return singleLine.length > PREVIEW_LIMIT ? `${singleLine.slice(0, PREVIEW_LIMIT)}…` : singleLine;
    };

    const toggleExpanded = (id) => {
        setExpandedId((prev) => (prev === id ? null : id));
    };

    const renderFeedList = () => (
        <div className="config-grid">
            {getSortedData().map((item) => {
                const expanded = expandedId === item.id;
                const long = isLongContent(item.description);
                return (
                    <div
                        key={item.id}
                        className={`wr-feed-card wr-feed-card--expandable ${expanded ? "is-expanded" : ""} ${long ? "has-more" : ""}`}
                    >
                        <button
                            type="button"
                            className="wr-feed-card__toggle"
                            onClick={() => toggleExpanded(item.id)}
                            aria-expanded={expanded}
                        >
                            <div className="wr-feed-card__title">
                                {item.name}
                                {long ? (
                                    <span className="wr-feed-card__chevron" aria-hidden="true">
                                        {expanded ? "▴" : "▾"}
                                    </span>
                                ) : null}
                            </div>
                            <div className="wr-feed-card__meta">
                                {[item.location_name || "Вся мережа", new Date(item.dtlm).toLocaleString("uk-UA")]
                                    .filter(Boolean)
                                    .join(" • ")}
                                {long && !expanded ? (
                                    <span className="wr-feed-card__more-hint">Є ще текст · натисніть, щоб розгорнути</span>
                                ) : null}
                            </div>
                            {!expanded ? (
                                <div className={`wr-feed-card__preview ${long ? "is-clamped" : ""}`}>
                                    {getPreviewText(item.description)}
                                </div>
                            ) : null}
                        </button>
                        {expanded ? (
                            <div className="wr-feed-card__body">
                                <div className="wr-feed-card__body-text">
                                    {linkifyPhoneText(item.description)}
                                </div>
                                {item.network_name ? (
                                    <div className="wr-feed-card__network">Мережа: {item.network_name}</div>
                                ) : null}
                                {long ? (
                                    <button
                                        type="button"
                                        className="wr-feed-card__collapse"
                                        onClick={() => setExpandedId(null)}
                                    >
                                        Згорнути
                                    </button>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );

    const renderDesktopView = () => (
        <div className="table-responsive">
            <table>
                <thead>
                    <tr>
                        <th onClick={() => toggleSort("name")} style={{ cursor: "pointer" }}>
                            Назва {sortBy === "name" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th onClick={() => toggleSort("description")} style={{ cursor: "pointer" }}>
                            Опис {sortBy === "description" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th>Дата</th>
                    </tr>
                </thead>
                <tbody>
                    {getSortedData().map((item) => {
                        const expanded = expandedId === item.id;
                        return (
                            <React.Fragment key={item.id}>
                                <tr
                                    onClick={() => toggleExpanded(item.id)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <td>{item.name}</td>
                                    <td>{expanded ? "▾ розгорнуто" : getPreviewText(item.description)}</td>
                                    <td style={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                                        {new Date(item.dtlm).toLocaleString("uk-UA")}
                                    </td>
                                </tr>
                                {expanded ? (
                                    <tr>
                                        <td colSpan={3}>
                                            <div className="wr-feed-card__body" style={{ padding: "8px 4px 12px" }}>
                                                {linkifyPhoneText(item.description)}
                                            </div>
                                        </td>
                                    </tr>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="operator-info-container">
            <div className="config-header">
                <div className="header-buttons">
                    <button
                        className="add-btn refresh toolbar-icon-btn"
                        onClick={loadInfo}
                        title="Оновити"
                        aria-label="Оновити"
                    >
                        <RotateCw size={16} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук за назвою, описом, аудиторією..."
                    />
                </div>
            </div>

            {message && <div className="message-banner">{message}</div>}

            {filteredInfo.length === 0 ? (
                <p style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    Немає доступної інформації.
                </p>
            ) : (
                window.innerWidth < 768 ? renderFeedList() : renderDesktopView()
            )}
        </div>
    );
};

export default OperatorInfoClients;
